import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, 
  Trash2, 
  QrCode, 
  MessageSquare, 
  Settings, 
  Eye, 
  RefreshCw,
  Wifi,
  WifiOff,
  Clock,
  Phone
} from 'lucide-react';
import toast from 'react-hot-toast';

const Dashboard = ({ user, onLogout }) => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newInstanceName, setNewInstanceName] = useState('');
  const [creatingInstance, setCreatingInstance] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    connected: 0,
    disconnected: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadInstances();
  }, []);

  const loadInstances = async () => {
    try {
      setLoading(true);
      console.log('🔍 Cargando instancias...');
      
      const response = await axios.get('/api/instances');
      const instancesData = response.data.instances || [];
      
      console.log(`📊 Cargadas ${instancesData.length} instancias`);
      
      // Debug: mostrar la primera instancia para verificar datos
      if (instancesData.length > 0) {
        console.log('🔍 Primera instancia recibida:', instancesData[0]);
        console.log('🏷️ Nombre de instancia:', instancesData[0].instanceName);
        console.log('👤 Nombre de perfil:', instancesData[0].profileName);
      }
      
      setInstances(instancesData);
      setStats({
        total: response.data.total || instancesData.length,
        connected: response.data.connected || instancesData.filter(i => i.status === 'connected').length,
        disconnected: response.data.disconnected || instancesData.filter(i => i.status === 'disconnected').length
      });
      
    } catch (error) {
      console.error('❌ Error al cargar instancias:', error);
      toast.error('Error al cargar las instancias');
    } finally {
      setLoading(false);
    }
  };

  const refreshInstances = async () => {
    try {
      setRefreshing(true);
      await loadInstances();
      toast.success('Instancias actualizadas');
    } catch (error) {
      toast.error('Error al actualizar instancias');
    } finally {
      setRefreshing(false);
    }
  };

  const createInstance = async (e) => {
    e.preventDefault();
    
    if (!newInstanceName.trim()) {
      toast.error('Por favor ingresa un nombre para la instancia');
      return;
    }

    try {
      setCreatingInstance(true);
      console.log(`🚀 Creando instancia: ${newInstanceName.trim()}`);
      
      const response = await axios.post('/api/instances', {
        instanceName: newInstanceName.trim()
      });

      if (response.data.success) {
        toast.success('Instancia creada exitosamente');
        setNewInstanceName('');
        loadInstances();
      }
    } catch (error) {
      console.error('❌ Error al crear instancia:', error);
      const errorMessage = error.response?.data?.error || 'Error al crear instancia';
      toast.error(errorMessage);
    } finally {
      setCreatingInstance(false);
    }
  };

  const deleteInstance = async (instanceName) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar la instancia "${instanceName}"? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      console.log(`🗑️ Eliminando instancia: ${instanceName}`);
      await axios.delete(`/api/instances/${instanceName}`);
      toast.success('Instancia eliminada exitosamente');
      loadInstances();
    } catch (error) {
      console.error('❌ Error al eliminar instancia:', error);
      toast.error('Error al eliminar instancia');
    }
  };

  const restartInstance = async (instanceName) => {
    try {
      console.log(`🔄 Reiniciando instancia: ${instanceName}`);
      await axios.post(`/api/instances/${instanceName}/restart`);
      toast.success('Instancia reiniciada exitosamente');
      setTimeout(() => loadInstances(), 2000); // Recargar después de 2 segundos
    } catch (error) {
      console.error('❌ Error al reiniciar instancia:', error);
      toast.error('Error al reiniciar instancia');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return '#10b981'; // Verde
      case 'connecting':
      case 'restarting':
        return '#f59e0b'; // Amarillo
      case 'disconnected':
        return '#ef4444'; // Rojo
      default:
        return '#6b7280'; // Gris
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected':
        return 'Conectado';
      case 'connecting':
        return 'Conectando';
      case 'restarting':
        return 'Reiniciando';
      case 'disconnected':
        return 'Desconectado';
      default:
        return 'Desconocido';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <Wifi size={16} />;
      case 'connecting':
      case 'restarting':
        return <Clock size={16} />;
      case 'disconnected':
        return <WifiOff size={16} />;
      default:
        return <WifiOff size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando instancias de Evolution API...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center flex-grow-1">
            <div className="text-left">
              <img 
                src="https://waengine.com/wp-content/uploads/WAEngine_logo_transparent_horiz-01.png" 
                alt="WAEngine" 
                title="WAEngine" 
                className="waengine-logo"
              />
              <h1 style={{ fontSize: '24px', marginBottom: '4px', marginTop: '8px' }}>
                WAEngine API Manager
              </h1>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Gestiona todas las instancias de WhatsApp
              </p>
            </div>
          </div>
          
          <div className="d-flex align-items-center gap-3">
            <button 
              onClick={refreshInstances} 
              className="btn-secondary btn-small"
              disabled={refreshing}
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Actualizando...' : 'Actualizar'}
            </button>
            
            <div className="user-info">
              <div className="text-end">
                <small className="text-muted d-block">Sesión activa</small>
                <span className="user-email">{user?.email}</span>
              </div>
              <button onClick={onLogout} className="logout-btn">
                🚪 Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Estadísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <MessageSquare size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Total Instancias</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ color: '#10b981' }}>
            <Wifi size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.connected}</h3>
            <p>Conectadas</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ color: '#ef4444' }}>
            <WifiOff size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.disconnected}</h3>
            <p>Desconectadas</p>
          </div>
        </div>
      </div>

      {/* Crear Nueva Instancia */}
      <div className="card">
        <h2>Crear Nueva Instancia</h2>
        <form onSubmit={createInstance} className="form">
          <input
            type="text"
            value={newInstanceName}
            onChange={(e) => setNewInstanceName(e.target.value)}
            placeholder="Nombre de la instancia (ej: cliente-empresa-001)"
            className="input-field"
            disabled={creatingInstance}
          />
          <button
            type="submit"
            className="btn-primary"
            disabled={creatingInstance || !newInstanceName.trim()}
          >
            {creatingInstance ? (
              <>
                <div className="spinner" style={{ width: '16px', height: '16px', marginRight: '8px' }}></div>
                Creando...
              </>
            ) : (
              <>
                <Plus size={16} />
                Crear Instancia
              </>
            )}
          </button>
        </form>
      </div>

      {/* Lista de Instancias */}
      <div className="card">
        <h2>Instancias Registradas ({instances.length})</h2>
        
        {instances.length === 0 ? (
          <div className="empty-state">
            <MessageSquare size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p>No hay instancias registradas</p>
            <p>Crea la primera instancia para comenzar</p>
          </div>
        ) : (
          <div className="instances-grid">
            {instances.map((instance, index) => (
              <div key={instance.instanceName || index} className="instance-card">
                <div className="instance-header">
                  <h3>{instance.instanceName}</h3>
                  <span
                    className="status-badge"
                    style={{ 
                      backgroundColor: getStatusColor(instance.status),
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {getStatusIcon(instance.status)}
                    {getStatusText(instance.status)}
                  </span>
                </div>
                
                <div className="instance-info">
                  {/* Mostrar número de teléfono solo si está conectado */}
                  {instance.status === 'connected' && instance.phoneNumber && (
                    <p>
                      <Phone size={14} style={{ display: 'inline', marginRight: '8px' }} />
                      <strong>Teléfono:</strong> {instance.phoneNumber}
                    </p>
                  )}
                  
                  {/* No mostrar profileName ya que es el nombre del dispositivo, no de la instancia */}
                  
                  {/* Mostrar fecha de creación si existe */}
                  {instance.created_at && (
                    <p><strong>Creada:</strong> {new Date(instance.created_at).toLocaleDateString()}</p>
                  )}
                  
                  {/* Mostrar última actualización */}
                  <p><strong>Actualizada:</strong> {new Date(instance.updated_at).toLocaleString()}</p>
                  
                  {/* Debug: mostrar estado original */}
                  {process.env.NODE_ENV === 'development' && instance.originalConnectionStatus && (
                    <p style={{ fontSize: '12px', color: '#666' }}>
                      <strong>Estado original:</strong> {instance.originalConnectionStatus}
                    </p>
                  )}
                </div>

                <div className="instance-actions">
                  <button
                    onClick={() => navigate(`/instance/${instance.instanceName}`)}
                    className="btn-primary btn-small"
                  >
                    <Eye size={16} />
                    Gestionar
                  </button>
                  
                  {instance.status !== 'open' && (
                    <button
                      onClick={() => navigate(`/instance/${instance.instanceName}`)}
                      className="btn-secondary btn-small"
                    >
                      <QrCode size={16} />
                      Conectar
                    </button>
                  )}
                  
                  <button
                    onClick={() => restartInstance(instance.instanceName)}
                    className="btn-secondary btn-small"
                  >
                    <RefreshCw size={16} />
                    Reiniciar
                  </button>
                  
                  <button
                    onClick={() => deleteInstance(instance.instanceName)}
                    className="btn-danger btn-small"
                  >
                    <Trash2 size={16} />
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="text-center mt-4">
        <small style={{ color: 'var(--waengine-text-muted)' }}>
          WAEngine API Manager | Made with ❤️ by InnovateIA
        </small>
      </div>
    </div>
  );
};

export default Dashboard;
